import { useState, useMemo, useDeferredValue } from "react";
import { Dropdown, Button } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { getRowKey } from "pages/media/components/storyboardEditor/helpers";
import { useAntTheme } from "hooks/useAntTheme";
import { BrandKitIcon } from "../../../../components/icons";
import { BoxArrowUpIcon, TextAaIcon } from "@phosphor-icons/react";
import { useFontGroups, useTranslate, useCustomInvalidate } from "#src/hooks";
import { FontItem, FONT_CATEGORIES } from "./types";
import { useFontSelectState } from "./hooks/useFontSelectState";
import { FontPreview } from "./components/FontPreview";
import { FontDropdownContent } from "./components/FontDropdownContent";
import { FontUploadModal } from "./FontUploadModal";
import { BrandKitAsset } from "../../types";
import { radius } from "@libertify/ui";

interface GroupedFontPickerProps {
  brandKitFonts: BrandKitAsset[];
  assetLibraryId?: string;
  onChange: (path: string, fontAssetId: string) => void;
  value?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  organizationId?: string;
  showUploadModal?: boolean;
  useCurrentFontLabel?: boolean;
  brandKitOnly?: boolean;
}

export const GroupedFontPicker = ({
  brandKitFonts,
  assetLibraryId,
  onChange,
  value,
  style,
  placeholder,
  organizationId,
  showUploadModal = true,
  useCurrentFontLabel = false,
  brandKitOnly = false,
}: GroupedFontPickerProps) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [openFontsUploadModal, setOpenFontsUploadModal] = useState(false);
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const { customInvalidate } = useCustomInvalidate();
  const {
    fontCategories: allCategories,
    allFontItems,
    updateFontsOptimistically,
    deleteFontOptimistically,
  } = useFontGroups({
    brandKitFonts,
    assetLibraryId,
    useCurrentFontLabel,
    brandKitOnly,
  });

  const filteredData = useMemo(() => {
    if (!deferredSearchTerm.trim()) {
      return {
        categories: allCategories,
        items: allFontItems,
      };
    }

    const filteredItems = allFontItems.filter((item) => {
      const searchTermLower = deferredSearchTerm.toLowerCase();
      const prettyNameLower = item.prettyName.toLowerCase();
      const fullNameLower = item.fullName.toLowerCase();
      const normalizeString = (str: string) =>
        str.replace(/[-_\s]+/g, " ").trim();
      const normalizedSearchTerm = normalizeString(searchTermLower);
      const normalizedPrettyName = normalizeString(prettyNameLower);
      const normalizedFullName = normalizeString(fullNameLower);
      return (
        normalizedPrettyName.includes(normalizedSearchTerm) ||
        normalizedFullName.includes(normalizedSearchTerm)
      );
    });

    const filteredCategories = allCategories
      .map((category) => ({
        ...category,
        families: category.families
          .map((family) => ({
            ...family,
            variants: family.variants.filter((variant) =>
              filteredItems.some((filtered) => filtered.key === variant.key)
            ),
          }))
          .filter((family) => family.variants.length > 0),
      }))
      .filter((category) => category.families.length > 0);

    return {
      categories: filteredCategories,
      items: filteredItems,
    };
  }, [allCategories, allFontItems, deferredSearchTerm]);

  const fontCategories = filteredData.categories;
  const allUnfilteredFonts = allFontItems;
  const {
    expandedFamilies,
    selectedFont,
    dropdownVisible,
    setSelectedFont,
    setDropdownVisible,
    toggleFamilyExpansion,
  } = useFontSelectState({
    value,
    searchTerm: deferredSearchTerm,
  });
  const iconMap = {
    [FONT_CATEGORIES.BRANDKIT_FONTS]: <BrandKitIcon width={15} height={15} />,
    [FONT_CATEGORIES.FONT_CATALOG]: <TextAaIcon size={20} weight="bold" />,
    [FONT_CATEGORIES.LIBRARY_FONTS]: <BoxArrowUpIcon size={20} weight="bold" />,
  };
  const categoriesWithIcons = fontCategories.map((category) => ({
    ...category,
    icon: iconMap[category.categoryType],
  }));

  const handleFontSelect = (fontItem: FontItem) => {
    if (!fontItem.font?.path) return;

    setSelectedFont(fontItem.key);
    setDropdownVisible(false);
    onChange(fontItem.font.path, getRowKey(fontItem.font));
  };

  const handleFamilyToggle = (familyName: string, keys: string[]) => {
    toggleFamilyExpansion(familyName, keys.includes(familyName));
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value.trim() && !dropdownVisible) {
      setDropdownVisible(true);
    }
  };

  const handleDropdownClose = () => {
    setDropdownVisible(false);
    setSearchTerm("");
  };

  const handleDropdownVisibilityChange = (visible: boolean) => {
    setDropdownVisible(visible);
    if (!visible) {
      setSearchTerm("");
    }
  };

  const getSelectedBackgroundColor = () => {
    return theme.colorBgContainerDisabled;
  };

  const selectedFontItem = allUnfilteredFonts.find(
    (item) => item.key === selectedFont
  );

  const invalidateAssetsList = () => {
    customInvalidate(["assetsList", assetLibraryId]).catch((error) => {
      console.error("Error invalidating assets list", error);
    });
  };

  const dropdownRender = () => (
    <FontDropdownContent
      searchTerm={searchTerm}
      onSearchChange={handleSearchChange}
      fontCategories={categoriesWithIcons}
      expandedFamilies={expandedFamilies}
      selectedFont={selectedFont}
      selectedBackgroundColor={getSelectedBackgroundColor()}
      onFontSelect={handleFontSelect}
      onFamilyToggle={handleFamilyToggle}
      // onExampleSectionToggle={handleExampleSectionToggle}
      onUploadFont={
        showUploadModal ? () => setOpenFontsUploadModal(true) : undefined
      }
      onDropdownClose={handleDropdownClose}
      assetLibraryId={assetLibraryId}
      onFontDeleteSuccess={(assetId) => {
        deleteFontOptimistically(assetId);
        invalidateAssetsList();
      }}
    />
  );

  return (
    <>
      <Dropdown
        popupRender={dropdownRender}
        trigger={["click"]}
        open={dropdownVisible}
        onOpenChange={handleDropdownVisibilityChange}
        placement="bottom"
        overlayStyle={{
          width: 350,
          height: "fit-content",
          // higher content will make the modal flick on render on small resolutions
          // TODO make it more customisable
          maxHeight: "calc(100vh - 140px)",
          backgroundColor: theme.colorBgElevated,
          padding: 0,
          borderRadius: radius.box,
          boxShadow: theme.boxShadowSecondary,
          overflow: "hidden",
        }}
      >
        <Button
          data-testid="grouped-fonts-select"
          style={{
            width: 150,
            ...style,
            justifyContent: "space-between",
            paddingRight: 8,
            paddingLeft: 8,
          }}
          icon={<DownOutlined style={{ opacity: 0.7 }} />}
          iconPosition="end"
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              textAlign: "left",
              display: "block",
              fontSize: 13,
            }}
          >
            {selectedFontItem ? (
              <FontPreview
                fontItem={selectedFontItem}
                hideTag={true}
                style={{
                  padding: 0,
                  textAlign: "left",
                  fontSize: 13,
                }}
              />
            ) : (
              placeholder || t("components.FontSelect.placeholder")
            )}
          </span>
        </Button>
      </Dropdown>
      <FontUploadModal
        openFontsUploadModal={openFontsUploadModal}
        setOpenFontsUploadModal={setOpenFontsUploadModal}
        organizationId={organizationId}
        assetLibraryId={assetLibraryId}
        onUploadFontComplete={(assets) => {
          updateFontsOptimistically(assets);
        }}
      />
    </>
  );
};
